// src/components/admin/RoleForm.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Autocomplete,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  useNavigate,
  useParams,
  useSearchParams,
  Link as RouterLink,
} from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { APP_HOME_PATH } from "../../config/routes";
import roleApi from "../../api/roles";
import { useNotify } from "../../utils/useNotify";
import { formatAxiosError } from "../../utils/apiError";

const TENANT_PERMISSION_OPTIONS = [
  { value: "manage_tenant", label: "Manage organization settings" },
  { value: "manage_users", label: "Manage users & invitations" },
  { value: "manage_roles", label: "Manage roles" },
  { value: "view_reports", label: "View reports" },
  { value: "manage_animals", label: "Manage animals" },
  { value: "manage_calving", label: "Manage calving" },
  { value: "view_financials", label: "View financials" },
  { value: "manage_milk", label: "Manage milk records" },
];

const PLATFORM_PERMISSION_OPTIONS = [
  { value: "manage_system", label: "Manage system (platform)" },
  { value: "view_all_tenants", label: "View all organizations" },
  { value: "impersonate_users", label: "Impersonate users" },
  ...TENANT_PERMISSION_OPTIONS,
];

const PROTECTED_ROLE_NAMES = new Set(
  ["tenantadmin", "manager", "staff", "bookkeeper"]
);

function isProtectedRoleName(name) {
  return PROTECTED_ROLE_NAMES.has(String(name || "").toLowerCase());
}

/** @param {number|null|undefined} tenantId undefined = still loading */
function baseOptionsForTenant(tenantId) {
  if (tenantId === undefined) return TENANT_PERMISSION_OPTIONS;
  if (tenantId === null) return PLATFORM_PERMISSION_OPTIONS;
  return TENANT_PERMISSION_OPTIONS;
}

function mergePermissionOptions(selectedValues, baseOptions) {
  const byValue = new Map(baseOptions.map((o) => [o.value, o]));
  const extra = (selectedValues || [])
    .filter((v) => v && !byValue.has(v))
    .map((v) => ({ value: v, label: `${v} (legacy)` }));
  return [...baseOptions, ...extra];
}

const emptyValues = { name: "", description: "", permissions: [] };

const RoleForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const notify = useNotify();

  const [pageLoading, setPageLoading] = useState(Boolean(isEdit));
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [roleMeta, setRoleMeta] = useState(null);
  const [initialValues, setInitialValues] = useState(emptyValues);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isSuperAdmin = currentUser?.roles?.some(
    (r) => r === "super_admin" || r?.name === "super_admin"
  );
  const queryTenantIdRaw = searchParams.get("tenant_id");
  const queryTenantId =
    queryTenantIdRaw != null && queryTenantIdRaw !== ""
      ? Number(queryTenantIdRaw)
      : null;
  const createTenantId =
    isEdit || !isSuperAdmin
      ? currentUser?.tenant_id ?? null
      : Number.isFinite(queryTenantId)
        ? queryTenantId
        : null;

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoadError(null);
      if (!isEdit || !id) {
        setRoleMeta({ tenant_id: createTenantId });
        setInitialValues(emptyValues);
        setPageLoading(false);
        return;
      }
      setPageLoading(true);
      try {
        const roleData = await roleApi.getRole(id);
        if (cancelled) return;
        setRoleMeta({
          name: roleData.name,
          tenant_id: roleData.tenant_id,
          assigned_user_count: roleData.assigned_user_count ?? 0,
        });
        setInitialValues({
          name: roleData.name,
          description: roleData.description ?? "",
          permissions: Array.isArray(roleData.permissions)
            ? roleData.permissions
            : [],
        });
      } catch (e) {
        if (!cancelled) setLoadError(formatAxiosError(e, "Could not load role"));
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, createTenantId]);

  const validationSchema = useMemo(
    () =>
      yup.object({
        name: isEdit ? yup.string() : yup.string().required("Name is required"),
        description: yup.string().required("Description is required"),
        permissions: yup.array().min(1, "Select at least one permission"),
      }),
    [isEdit]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        if (isEdit) {
          await roleApi.updateRole(id, {
            description: values.description,
            permissions: values.permissions,
          });
          notify.success("Role updated.");
        } else {
          await roleApi.createRole(
            {
              name: values.name,
              description: values.description,
              permissions: values.permissions,
            },
            createTenantId
          );
          notify.success("Role created.");
        }
        navigate("/admin/roles");
      } catch (error) {
        notify.error(formatAxiosError(error, "Could not save role"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const basePermissionOptions = useMemo(() => {
    if (!roleMeta) return TENANT_PERMISSION_OPTIONS;
    return baseOptionsForTenant(roleMeta.tenant_id);
  }, [roleMeta]);

  const mergedOptions = useMemo(
    () => mergePermissionOptions(formik.values.permissions, basePermissionOptions),
    [formik.values.permissions, basePermissionOptions]
  );

  const canDelete =
    isEdit &&
    roleMeta &&
    !isProtectedRoleName(roleMeta.name) &&
    (roleMeta.assigned_user_count ?? 0) === 0;

  const deleteBlockedReason = useMemo(() => {
    if (!isEdit || !roleMeta) return null;
    if (isProtectedRoleName(roleMeta.name)) {
      return "Built-in organization roles cannot be deleted.";
    }
    const n = roleMeta.assigned_user_count ?? 0;
    if (n > 0) {
      return `Remove this role from ${n} user${n === 1 ? "" : "s"} before deleting.`;
    }
    return null;
  }, [isEdit, roleMeta]);

  const handleDelete = async () => {
    if (!canDelete) return;
    try {
      setDeleting(true);
      await roleApi.deleteRole(id);
      notify.success("Role deleted.");
      setDeleteOpen(false);
      navigate("/admin/roles");
    } catch (error) {
      notify.error(formatAxiosError(error, "Could not delete role"));
    } finally {
      setDeleting(false);
    }
  };

  if (pageLoading && isEdit) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 3, maxWidth: 640 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
            Dashboard
          </Link>
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to="/admin/roles"
          >
            Roles
          </Link>
          <Typography color="text.primary">Edit</Typography>
        </Breadcrumbs>
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
        <Button variant="contained" component={RouterLink} to="/admin/roles">
          Back to roles
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 720, mx: "auto" }}>
      <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
        <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
          Dashboard
        </Link>
        <Link
          component={RouterLink}
          underline="hover"
          color="inherit"
          to="/admin/roles"
        >
          Roles
        </Link>
        <Typography color="text.primary">
          {isEdit
            ? roleMeta?.name
              ? `Edit: ${roleMeta.name}`
              : "Edit role"
            : "Create role"}
        </Typography>
      </Breadcrumbs>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          component={RouterLink}
          to="/admin/roles"
        >
          Back to list
        </Button>
      </Stack>

      <Typography variant="h4" gutterBottom>
        {isEdit ? "Edit role" : "Create role"}
      </Typography>

      {isEdit && roleMeta?.tenant_id != null && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Organization-scoped role ·{" "}
          {roleMeta.assigned_user_count === 0
            ? "No users currently have this role."
            : `${roleMeta.assigned_user_count} user${
                roleMeta.assigned_user_count === 1 ? "" : "s"
              } have this role.`}
        </Typography>
      )}

      {!isEdit && isSuperAdmin && !Number.isFinite(queryTenantId) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Open this screen from <strong>Roles</strong> after selecting an
          organization, or add <code>?tenant_id=…</code> to the URL.
        </Alert>
      )}

      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2.5}>
          {!isEdit ? (
            <TextField
              fullWidth
              name="name"
              label="Role name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          ) : (
            <TextField
              fullWidth
              label="Role name"
              value={formik.values.name}
              disabled
              helperText="Name cannot be changed. Create a new role if you need a different name."
            />
          )}

          <TextField
            fullWidth
            name="description"
            label="Description"
            multiline
            minRows={3}
            value={formik.values.description}
            onChange={formik.handleChange}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            helperText={formik.touched.description && formik.errors.description}
          />

          <Autocomplete
            multiple
            disableCloseOnSelect
            options={mergedOptions}
            getOptionLabel={(o) => (typeof o === "string" ? o : o.label)}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            value={mergedOptions.filter((o) =>
              formik.values.permissions.includes(o.value)
            )}
            onChange={(_, newValue) => {
              formik.setFieldValue(
                "permissions",
                newValue.map((o) => o.value),
                true
              );
            }}
            renderOption={(props, option, { selected }) => {
              const { key, ...rest } = props;
              return (
                <li key={key} {...rest}>
                  <Checkbox checked={selected} sx={{ mr: 1 }} size="small" />
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.value}
                    </Typography>
                  </Box>
                </li>
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.value}
                  label={option.label}
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Permissions"
                placeholder="Search and select…"
                error={
                  formik.touched.permissions &&
                  Boolean(formik.errors.permissions)
                }
                helperText={
                  (formik.touched.permissions && formik.errors.permissions) ||
                  "Multiple selections allowed. Use search to filter the list."
                }
              />
            )}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Create role"}
            </Button>
            <Button
              variant="outlined"
              component={RouterLink}
              to="/admin/roles"
            >
              Cancel
            </Button>
            {isEdit && (
              <Button
                color="error"
                variant="outlined"
                disabled={!canDelete}
                onClick={() => setDeleteOpen(true)}
                sx={{ ml: { sm: "auto" } }}
              >
                Delete role
              </Button>
            )}
          </Stack>

          {isEdit && deleteBlockedReason && (
            <Alert severity="info">{deleteBlockedReason}</Alert>
          )}
        </Stack>
      </form>

      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)}>
        <DialogTitle>Delete this role?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This cannot be undone. Only roles with no assigned users can be
            removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleForm;
